import React from "react";
import BasePage from "../BasePage";
import BGImage from "../../assets/img/Designer.webp";
import imgR from "../../assets/img/img-hero-r.webp";
import imgL from "../../assets/img/img-hero-l.webp";

const PrivacyPolicy: React.FC = () => {
  return (
    <BasePage
      title="Privacy Policy - UC Waves"
      description="Learn how UC Waves collects, uses, and protects your information when you use our website or dApp."
      keywords="Privacy Policy, UC Waves, Data Collection, Security, Cookies"
    >
      <main
        className="relative flex flex-col items-center justify-center pt-28 bg-cover bg-center min-h-screen px-6"
        style={{ backgroundImage: `url(${BGImage})` }}
      >
        {/* Header with images */}

        <div className="flex items-center gap-8 justify-between w-full max-w-5xl mb-10">
          <img
            src={imgL}
            alt="Hero decorative left"
            width={256} // largeur "md:w-40" en px
            height={256} // même valeur pour un carré
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
          <h1 className="text-6xl font-bold text-white text-center drop-shadow-lg">
            Privacy Policy
          </h1>

          <img
            src={imgR}
            alt="Hero decorative right"
            width={256}
            height={256}
            className="z-[3] w-24 md:w-40 xl:w-64 animate-fade-in-up"
          />
        </div>

        {/* Content container */}
        <div className="bg-white bg-opacity-90 backdrop-blur-md p-8 max-w-5xl text-gray-800 drop-shadow-lg mb-16">
          <p className="text-sm text-gray-600 mb-8">
            Last updated: <span className="font-semibold">06.12.2025</span>
          </p>

          <p className="mb-6">
            UcWaves is committed to respecting and protecting your privacy. This
            Privacy Policy outlines how we collect, use, and protect your
            information when you access our website at{" "}
            <a
              href="https://www.ucwaves.com"
              className="text-primary-dark hover:underline"
            >
              https://www.ucwaves.com
            </a>{" "}
            (“Website”) or interact with our decentralized application (“dApp”).
          </p>
          <p className="mb-6">
            By using UcWaves, you agree to the terms outlined in this Privacy
            Policy. If you do not agree, please discontinue use of the Website
            and dApp immediately.
          </p>

          <ol className="list-decimal list-inside space-y-6">
            <li>
              <h2 className="text-2xl font-semibold mb-2">Overview</h2>
              <p className="mb-2">
                UcWaves is a non-custodial, decentralized, privacy-focused
                platform. We strive to collect as little personal data as
                possible, and in most cases, we do not collect any personally
                identifiable information (PII) whatsoever.
              </p>
              <p>Key principles of our Privacy Policy:</p>
              <ul className="list-disc list-inside ml-6 space-y-1 mt-2">
                <li>We don’t collect what we don’t need</li>
                <li>We don’t store what we don’t use</li>
                <li>We never sell or rent user data</li>
                <li>You control your wallet and on-chain identity</li>
                <li>
                  We design our systems with data minimization and user
                  sovereignty at the core
                </li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                What We Don’t Collect
              </h2>
              <p className="mb-2">When you access or use UcWaves, we do not:</p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  Ask for your name, email, phone number, or postal address
                </li>
                <li>
                  Track your IP address, browser fingerprints, or physical
                  location
                </li>
                <li>
                  Log your device type, operating system, or browser history
                </li>
                <li>Use invasive analytics tools that store behavioral data</li>
                <li>Store or share wallet private keys or seed phrases</li>
                <li>
                  Know or retain your token balances, trades, or staking
                  positions
                </li>
              </ul>
              <p className="mt-2">
                In other words, we do not collect personal data under the
                definitions set forth by the General Data Protection Regulation
                (GDPR), California Consumer Privacy Act (CCPA), or other global
                privacy laws.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                What We Might Collect (Minimally)
              </h2>
              <p className="mb-2">
                While we aim to be data-minimal, certain optional or
                non-invasive data may be collected to support performance,
                security, or usability. These include:
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-1">
                a. Wallet Public Address
              </h3>
              <p className="mb-2">
                When you connect your Web3 wallet to the dApp (e.g., via
                MetaMask or WalletConnect), we may temporarily display your
                public wallet address on the frontend to personalize the
                experience (e.g., to show your stake or opinion status). This
                address:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Is publicly visible on the blockchain</li>
                <li>Is not stored on any UcWaves server</li>
                <li>Is not associated with your real-world identity by us</li>
              </ul>

              <h3 className="text-xl font-semibold mt-4 mb-1">
                b. Session Metadata (Optional)
              </h3>
              <p className="mb-2">
                To improve user experience, we may temporarily use browser
                session storage to:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Remember if you've connected your wallet</li>
                <li>Track selected theme or language preference</li>
                <li>Store non-identifying UI state (e.g., current tab)</li>
              </ul>
              <p className="mt-2">
                None of this data is stored on a server, and it is deleted when
                you close your browser tab.
              </p>

              <h3 className="text-xl font-semibold mt-4 mb-1">
                c. Aggregated, Anonymous Analytics (Optional)
              </h3>
              <p className="mb-2">
                We may use privacy-preserving, anonymized analytics (e.g.,
                Plausible, Ackee) that do not use cookies, IP tracking, or
                persistent identifiers. These tools help us understand:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>How many users accessed the dApp</li>
                <li>Which pages were visited most</li>
                <li>Which features are used frequently</li>
              </ul>
              <p className="mt-2">
                This data is not personally identifiable, is never sold or
                shared, and is used exclusively to improve the dApp.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                On-Chain Data Transparency
              </h2>
              <p className="mb-2">
                All interactions with the UcWaves protocol—staking, burning,
                voting, minting—are recorded on public blockchains (e.g., Base
                L2). This means:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Your transactions are transparent and immutable</li>
                <li>
                  They can be viewed by anyone using a blockchain explorer
                </li>
                <li>UcWaves does not control or anonymize on-chain activity</li>
              </ul>
              <p className="mt-2">
                We do not associate wallet addresses with identities, and we do
                not store any off-chain mapping between users and blockchain
                activity.
              </p>
              <p className="mt-2">
                If you want greater privacy on-chain, we recommend using
                privacy-preserving tools such as:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Wallets with no reuse of addresses</li>
                <li>Mixers or shielded protocols (where legal)</li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Cookie Policy</h2>
              <p className="mb-2">By default, the UcWaves platform:</p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Does not use cookies for tracking</li>
                <li>Does not store third-party cookies</li>
                <li>Does not implement behavioral ad pixels</li>
              </ul>
              <p className="mt-2">
                If in the future we introduce optional features that require
                cookies (e.g., login to a community forum), we will:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Provide full disclosure in a cookie banner</li>
                <li>
                  Request explicit consent before setting any non-essential
                  cookies
                </li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Data Storage and Retention
              </h2>
              <p className="mb-2">
                UcWaves does not store any user data on centralized databases or
                cloud services. The dApp runs on a fully client-side
                architecture, where all sensitive operations are performed
                locally in your browser.
              </p>
              <p>
                Any data temporarily stored (e.g., wallet address in memory) is:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Not persisted across sessions</li>
                <li>Not stored on UcWaves servers</li>
                <li>Not accessible to anyone other than the user</li>
              </ul>
              <p className="mt-2">
                If we ever implement user accounts for auxiliary services (e.g.,
                governance discussion forums), the associated Privacy Policy for
                those services will be separate and made clear.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Data Sharing and Third Parties
              </h2>
              <p className="mb-2">
                We do not share, sell, rent, or trade any user data with third
                parties. We do not partner with any advertising networks, data
                brokers, or analytics vendors that collect personal data.
              </p>
              <p>In the event we use any third-party tools, they will:</p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Be privacy-focused (e.g., zero-logging analytics)</li>
                <li>Be evaluated for GDPR and CCPA compliance</li>
                <li>Never receive user-identifiable information</li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Children's Privacy
              </h2>
              <p>
                UcWaves is not intended for children under the age of 18. We do
                not knowingly collect data from anyone under 18. If you are a
                parent or guardian and believe your child has interacted with
                the platform in violation of this policy, please contact us at{" "}
                <span className="font-semibold">[Insert Email]</span>.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Your Rights (If Applicable)
              </h2>
              <p className="mb-2">
                Although we do not store personal data, we support the spirit of
                international privacy regulations, including:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>GDPR (EU)</li>
                <li>CCPA (California)</li>
                <li>PIPEDA (Canada)</li>
              </ul>
              <p className="mt-2">
                If any minimal data is ever stored (e.g., in optional services),
                users will always retain rights including:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>The right to access any personal data we may hold</li>
                <li>The right to request correction or deletion</li>
                <li>The right to withdraw consent</li>
                <li>The right to data portability</li>
                <li>
                  The right to lodge a complaint with a regulatory authority
                </li>
              </ul>
              <p className="mt-2">
                To exercise your rights, contact us at:{" "}
                <span className="font-semibold">
                  [Insert Legal/Privacy Email]
                </span>
                .
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Security Measures</h2>
              <p className="mb-2">
                Although we don’t store user data, we prioritize platform
                security at every level:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  Smart contracts are rigorously tested and may be audited
                </li>
                <li>We follow best practices in secure dApp development</li>
                <li>Frontend assets are served over HTTPS</li>
                <li>
                  The dApp contains no embedded wallet credentials or secrets
                </li>
              </ul>
              <p className="mt-2">
                Users must also take proactive steps to secure their wallets:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Use trusted wallet software</li>
                <li>Store seed phrases offline in secure locations</li>
                <li>Beware of phishing links and impersonation attempts</li>
              </ul>
              <p className="mt-2">
                UcWaves cannot recover lost wallets or funds. You are solely
                responsible for your own security.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Data Breaches</h2>
              <p className="mb-2">
                Since we do not collect or store personal data, the risk of a
                traditional data breach is extremely low. However, in the event
                that a breach affecting user interactions (e.g., frontend
                compromise or DNS hijacking) occurs:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  We will disclose the breach publicly via our official channels
                </li>
                <li>We will advise users on steps to protect themselves</li>
                <li>We will take immediate action to remediate the issue</li>
                <li>
                  We will follow responsible disclosure principles and comply
                  with legal obligations where applicable
                </li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Hosting and Infrastructure
              </h2>
              <p className="mb-2">
                Our website is hosted on secure, privacy-conscious
                infrastructure providers. We avoid providers known to engage in
                surveillance or user profiling.
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Use firewalls and DDoS protection</li>
                <li>Minimize logging and error tracking</li>
                <li>Ensure code and content integrity via version control</li>
                <li>
                  We do not allow public write access or open database endpoints
                </li>
              </ul>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Third-Party Wallet Providers
              </h2>
              <p>
                When you connect to UcWaves via a wallet (e.g., MetaMask,
                Coinbase Wallet), the wallet provider may collect data about
                your device, IP address, and interactions. These providers are
                independent entities, and their privacy policies govern their
                actions—not ours.
              </p>
              <p className="mt-2">
                We encourage you to review the privacy policies of any wallet
                software or browser extension you use.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Use of Decentralized Services
              </h2>
              <p className="mb-2">
                UcWaves may rely on or integrate with decentralized services,
                such as:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>IPFS or Arweave for content delivery</li>
                <li>On-chain oracles for price feeds</li>
                <li>Decentralized identity frameworks in the future</li>
              </ul>
              <p className="mt-2">
                These services have no centralized data collection capability.
                They are designed to preserve censorship resistance and
                decentralization. However, content stored on public
                decentralized networks may be permanently retrievable.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Changes to This Policy
              </h2>
              <p className="mb-2">
                We reserve the right to update or modify this Privacy Policy at
                any time. Changes will be posted on this page with an updated
                revision date. If significant changes are made, we may notify
                users through our Website or dApp.
              </p>
              <p className="mt-2">
                Your continued use of the UcWaves platform after such changes
                constitutes your acceptance of the revised policy.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">
                Opt-In Features (If Introduced)
              </h2>
              <p className="mb-2">
                If we introduce features that require personal information
                (e.g., email signup for a newsletter), it will always be:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>Clearly opt-in</li>
                <li>Consent-based</li>
                <li>Revocable at any time</li>
                <li>Governed by a separate, focused privacy disclosure</li>
              </ul>
              <p className="mt-2">
                Such data will never be shared or sold and will be processed
                with maximum security.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Jurisdiction</h2>
              <p>
                UcWaves is a global platform and may be accessed from
                jurisdictions with different privacy laws. While we aim to be
                privacy-respecting worldwide, your jurisdiction may have
                specific rights or responsibilities beyond the scope of this
                policy.
              </p>
              <p className="mt-2">
                You are responsible for understanding and complying with your
                local data protection regulations.
              </p>
            </li>

            <li>
              <h2 className="text-2xl font-semibold mb-2">Contact Us</h2>
              <p>
                If you have any questions, complaints, or concerns regarding
                this Privacy Policy or the UcWaves platform’s handling of data,
                please contact:
              </p>
              <ul className="list-disc list-inside ml-6 space-y-1">
                <li>
                  Email:{" "}
                  <span className="font-semibold">Contact@ucwaves.com</span>
                </li>
                <li>
                  Website:{" "}
                  <a
                    href="https://www.ucwaves.com"
                    className="text-primary-dark hover:underline"
                  >
                    https://www.ucwaves.com
                  </a>
                </li>
              </ul>
              <p className="mt-4">
                We are committed to transparency, security, and community trust.
              </p>
            </li>
          </ol>
        </div>
      </main>
    </BasePage>
  );
};

export default PrivacyPolicy;
